import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { login } from '../../services/api';
import { useAuth, SESSION_MODES, applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginUser, user } = useAuth();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [branding, setBranding] = useState(null); // { name, logo_url, primary_color }

  const tenantSlug = searchParams.get('tenant');

  // Redirect already-authenticated users
  useEffect(() => {
    if (user) navigate(user.mode === SESSION_MODES.MASTER_GLOBAL ? '/master' : '/app', { replace: true });
  }, [user, navigate]);

  // Se chegou aqui por logout de inatividade, mostra toast explicando o motivo.
  // Flag é setada no AuthContext.logoutInactivity e consumida (removida) aqui.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('atendo_logout_reason');
      if (reason === 'inactivity') {
        toast('Sua sessão expirou por inatividade (1 hora). Faça login novamente para continuar.', { icon: '🔒', duration: 6000 });
        sessionStorage.removeItem('atendo_logout_reason');
      }
    } catch {}
  }, []);

  // Load tenant branding when ?tenant=slug is in the URL
  useEffect(() => {
    if (!tenantSlug) return;
    axios.get(`${API_URL}/public/${tenantSlug}`)
      .then(r => {
        const t = r.data.tenant;
        if (t) setBranding({ name: t.name, logo_url: t.logo_url, primary_color: t.primary_color || '#2563eb' });
      })
      .catch(() => {});
  }, [tenantSlug]);

  // Apply tenant branding (primary + variação dark gerada automaticamente)
  useEffect(() => {
    if (branding?.primary_color) {
      applyTenantBranding(branding.primary_color);
    }
    return () => clearTenantBranding();
  }, [branding]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('Preencha email e senha'); return; }
    setLoading(true);
    try {
      const payload = tenantSlug ? { ...form, tenant_slug: tenantSlug } : form;
      const res = await login(payload);
      const { token, user } = res.data;
      loginUser(token, user);
      if (user.must_change_password) {
        toast('⚠️ Você deve alterar sua senha no primeiro acesso.', { icon: '🔑', duration: 5000 });
      }
      navigate(user.mode === SESSION_MODES.MASTER_GLOBAL ? '/master' : '/app', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao fazer login';
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          {branding ? (
            <>
              {branding.logo_url
                ? <img src={branding.logo_url} alt={branding.name} style={{ height:64, objectFit:'contain', marginBottom:10, borderRadius:8 }} />
                : <div style={{ width:64, height:64, background:'var(--primary)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'white', fontSize:26, margin:'0 auto 10px' }}>
                    {branding.name?.[0]?.toUpperCase() || 'A'}
                  </div>
              }
              <h1>{branding.name}</h1>
              <p>Acesse sua conta</p>
            </>
          ) : (
            <>
              <img src="/logo-full.svg" alt="AtendoApp" style={{ height:48, objectFit:'contain', marginBottom:10 }} />
              <p>Plataforma de agendamentos SaaS</p>
            </>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="badge-danger" style={{ borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, display:'block', fontWeight:500 }}>
              ⚠️ {error}
            </div>
          )}
          <div className="form-group">
            <label>E-mail</label>
            <input type="email" placeholder="seu@email.com" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} autoFocus autoComplete="email" />
          </div>
          <div className="form-group">
            <label>Senha</label>
            <input type="password" placeholder="••••••••" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} autoComplete="current-password" />
          </div>
          <button type="submit" className={`btn btn-primary${loading?' btn-loading':''}`}
            style={{ width:'100%', padding:11, fontSize:14, marginTop:4 }} disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        <p style={{ textAlign:'center', marginTop:16, fontSize:13 }}>
          <Link to={`/forgot-password${tenantSlug ? `?tenant=${tenantSlug}` : ''}`} style={{ color:'var(--primary)', fontWeight:500, textDecoration:'none' }}>
            Esqueceu sua senha?
          </Link>
        </p>
        {/* HOMOLOGAÇÃO: signup oculto até lançamento
        {!branding && (
          <p style={{ textAlign:'center', marginTop:14, fontSize:13, color:'var(--gray-600)' }}>
            Não tem conta?{' '}
            <Link to="/signup" style={{ color:'var(--primary)', fontWeight:600, textDecoration:'none' }}>
              Criar conta grátis
            </Link>
          </p>
        )}
        */}
        {!branding && (
          <p style={{ textAlign:'center', marginTop:10, fontSize:12 }}>
            <a href="/" style={{ color:'var(--gray-500)', textDecoration:'none' }}>
              ← Voltar para atendoapp.com
            </a>
          </p>
        )}
        <p style={{ textAlign:'center', marginTop:24, fontSize:12, color:'var(--gray-400)' }}>
          {branding ? `Desenvolvido por AtendoApp` : `AtendoApp © ${new Date().getFullYear()}`}
        </p>
      </div>
    </div>
  );
}
